/**
 * WHICH BANNER REGION `BillingGate` PUTS ABOVE THE APP, as a pure decision.
 *
 * The gate renders SOLELY from the server-owned `effectiveRestrictions`
 * (`banners`), plus three request-time signals that are not collections
 * restrictions (the PM-setup nudge and the co-pay notice; spec 2026-07-20-001
 * D-5/D-5a, P-2). At most one region is up; `kind` is also the wrapper's React
 * key, so a swap between regions is a remount and the banner height is re-read
 * in the same commit (see `BillingGate`).
 *
 *  - `hard-lock`: hard lock with mission-critical grace (banner
 *    `hard_lock_grace`). Exclusive with the healthy stack.
 *  - `soft-lock`: soft lock / escalating / card-failure warning (banners
 *    `soft_lock` / `card_failure_warning`). Also suppresses the healthy stack.
 *  - `standing`: the conditions-driven healthy stack, any of:
 *      1. payment-method setup — an auto_charge org with a live subscription
 *         but no card on file. Shown to EVERYONE (admins get an add-card CTA,
 *         members a "let your admin know" prompt). Kept to a scope the broker
 *         wall applies to: under `/cs` it sat over every Customer screen for a
 *         hire who cannot act on it (journeys X14/D10, 2026-09-10);
 *      2. co-pay failure notice (04/WS-11) — org-admins only. `!= null`: an
 *         older cached payload lacks the field;
 *      3. outstanding-invoice balance (`payment_overdue_warning`) — org-admins;
 *      4. bank payment processing (`payment_processing`, a PayTo debit in
 *         flight; spec billing/2026-10-01-009 D7) — org-admins only.
 *
 * Org-admin CTAs gate on `identity.isAdmin` (includes super-admins), NOT the
 * narrow billing-admin flag `gateStatus.isAdmin`.
 */
import { billingWallApplies, type PlatformScope } from "@/domain/scope/platform-scope"
import type { BillingGateStatusResponse } from "@/contexts/billing/core/presentation/rpc/billing-lockout-rpc"

type GateStatus = typeof BillingGateStatusResponse.Type
type Banner = NonNullable<GateStatus["effectiveRestrictions"]>["banners"][number]

export interface StandingBanners {
  readonly paymentMethodSetup: boolean
  readonly copay: boolean
  readonly outstanding: boolean
  readonly processing: boolean
}

export type BillingBannerRegion =
  | { readonly kind: "hard-lock" }
  | { readonly kind: "soft-lock" }
  | ({ readonly kind: "standing" } & StandingBanners)

export interface BillingBannerRegionInput {
  readonly gateStatus: Pick<GateStatus, "paymentMethodSetupNeeded" | "copayFailure">
  readonly banners: ReadonlyArray<Banner>
  readonly isAdmin: boolean
  readonly surfaceScope: PlatformScope
}

export const billingBannerRegionOf = ({
  gateStatus,
  banners,
  isAdmin,
  surfaceScope,
}: BillingBannerRegionInput): BillingBannerRegion | null => {
  if (banners.includes("hard_lock_grace")) return { kind: "hard-lock" }
  if (banners.includes("soft_lock") || banners.includes("card_failure_warning")) return { kind: "soft-lock" }
  const standing: StandingBanners = {
    paymentMethodSetup: gateStatus.paymentMethodSetupNeeded === true && billingWallApplies(surfaceScope),
    copay: isAdmin && gateStatus.copayFailure != null,
    outstanding: isAdmin && banners.includes("payment_overdue_warning"),
    processing: isAdmin && banners.includes("payment_processing"),
  }
  return standing.paymentMethodSetup || standing.copay || standing.outstanding || standing.processing
    ? { kind: "standing", ...standing }
    : null
}
